import { get } from 'lodash-es'

import { type FormRule } from '../composables/useForm.ts'
import { i18nForm as i18n } from './i18n.ts'

export async function* validateRule<TForm extends Record<string, any>>({ rule, prop, form }: { rule: FormRule; prop: keyof TForm; form: TForm }) {
  const value = get(form, prop)

  const messages = i18n

  if (rule.required && (!value || `${value}`.trim().length === 0) && !(rule.type === 'boolean' && typeof value === 'boolean') && !(typeof value === 'number' && value === 0)) {
    yield createError(rule, rule.message ?? messages.required)
  }

  if (rule.min !== undefined && (typeof value === 'number' ? value < rule.min : value?.length < rule.min)) {
    yield createError(rule, rule.message ?? (typeof value === 'string' ? messages.minString : messages.min))
  }

  if (rule.max !== undefined && (typeof value === 'number' ? value > rule.max : value?.length > rule.max)) {
    yield createError(rule, rule.message ?? (typeof value === 'string' ? messages.maxString : messages.max))
  }

  if (rule.match && get(form, rule.match) !== value) {
    yield createError(rule, rule.message ?? messages.match)
  }

  if (rule.mismatch && get(form, rule.mismatch) === value) {
    yield createError(rule, rule.message ?? messages.mismatch)
  }

  if (rule.gt !== undefined && !isGreaterThan({ value, field: rule.gt, form })) {
    yield createError(rule, rule.message ?? messages.gt)
  }

  if (rule.gte !== undefined && !isGreaterThanOrEqualTo({ value, field: rule.gte, form })) {
    yield createError(rule, rule.message ?? messages.gte)
  }

  if (rule.lt !== undefined && !isLesserThan({ value, field: rule.lt, form })) {
    yield createError(rule, rule.message ?? messages.lt)
  }

  if (rule.lte !== undefined && !isLesserThanOrEqualTo({ value, field: rule.lte, form })) {
    yield createError(rule, rule.message ?? messages.lte)
  }

  if (rule.type && !isType({ type: rule.type, value })) {
    yield createError(rule, rule.message ?? messages.type)
  }

  if (rule.in && !isIn({ options: rule.in, value })) {
    yield createError(rule, rule.message ?? messages.in)
  }

  if (rule.validator) {
    const result = await rule.validator(value, form)
    if (result !== true) {
      if (result === false) {
        yield createError(rule, rule.message ?? '')
      } else {
        yield createError(rule, result.message ?? rule.message)
      }
    }
  }

  if (rule.validators) {
    for (const validator of rule.validators) {
      const result = await validator.validator(value, form)
      if (result !== true) {
        if (result === false) {
          yield createError(rule, validator.message ?? rule.message)
        } else {
          yield createError(rule, result.message ?? validator.message ?? rule.message)
        }
      }
    }
  }
}

function isType({ type, value }: { type: string; value: any }) {
  if (type === 'array') {
    return Array.isArray(value)
  }

  return type === typeof value
}

function isIn({ options, value }: { options: string | string[]; value: string | string[] }) {
  if (typeof options === 'string') {
    options = options.split(',').map((option) => option.trim())
  }

  if (Array.isArray(value)) {
    return value.every((item) => options.includes(item))
  }

  return options.includes(value)
}

function isGreaterThan({ value, field, form }: { value: number | string; field: string | number; form: Record<string, any> }) {
  const { v, f } = transform({ value, field, form })

  return v > f
}

function isGreaterThanOrEqualTo({ value, field, form }: { value: number | string; field: string | number; form: Record<string, any> }) {
  const { v, f } = transform({ value, field, form })

  return v >= f
}

function isLesserThan({ value, field, form }: { value: number | string; field: string | number; form: Record<string, any> }) {
  const { v, f } = transform({ value, field, form })

  return v < f
}

function isLesserThanOrEqualTo({ value, field, form }: { value: number | string; field: string | number; form: Record<string, any> }) {
  const { v, f } = transform({ value, field, form })

  return v <= f
}

function transform({ value, field, form }: { value: number | string; field: string | number; form: Record<string, any> }) {
  const v = Number(value)
  let f = field
  if (typeof field !== 'number') {
    f = get(form, field, 0)
  }

  return { v, f: Number(f) }
}

function createError(rule: FormRule, message: string) {
  if (message.includes('$match') && 'match' in rule) {
    return message.replace('$match', `${rule.match}`)
  }
  if (message.includes('$mismatch') && 'mismatch' in rule) {
    return message.replace('$mismatch', `${rule.mismatch}`)
  }
  if (message.includes('$min') && 'min' in rule) {
    return message.replace('$min', `${rule.min}`)
  }

  if (message.includes('$max') && 'max' in rule) {
    return message.replace('$max', `${rule.max}`)
  }

  if (message.includes('$gt') && 'gt' in rule) {
    return message.replace('$gt', rule?.gtLabel ? rule.gtLabel : `${rule.gt}`)
  }

  if (message.includes('$gte') && 'gte' in rule) {
    return message.replace('$gte', rule?.gteLabel ? rule.gteLabel : `${rule.gte}`)
  }

  if (message.includes('$lt') && 'lt' in rule) {
    return message.replace('$lt', rule?.ltLabel ? rule.ltLabel : `${rule.lt}`)
  }

  if (message.includes('$lte') && 'lte' in rule) {
    return message.replace('$lte', rule?.lteLabel ? rule.lteLabel : `${rule.lte}`)
  }

  if (message.includes('$type') && rule.type) {
    return message.replace('$type', rule.type)
  }

  if (message.includes('$in') && rule.in) {
    let replacement = rule.in
    if (Array.isArray(replacement)) {
      replacement = replacement.join(', ')
    }

    return message.replace('$in', replacement)
  }

  return message
}
